// src/pages/VehicleV2/BillAddSpares.jsx
// Design aligned with ProductionBOMListing
// Writes sparesIds / sparesDetIds / sparesDeduction into billGen_selection_${vehicleId}

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaFileInvoiceDollar,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/billAddSpares`;

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();
const clean = (v) => String(v ?? "").trim();
const isDDMMYYYY = (s) =>
  typeof s === "string" && /^(\d{2})\/(\d{2})\/(\d{4})$/.test(s);
const parseDDMMYYYY = (s) => {
  if (!isDDMMYYYY(s)) return null;
  const [dd, mm, yyyy] = s.split("/").map((x) => Number(x));
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  dt.setHours(0, 0, 0, 0);
  return dt;
};
const numOr0 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const defaultFilters = { fromDate: "", toDate: "", materialCode: "", materialName: "" };

const mapRow = (r) => ({
  storesIssueDetId: toInt(
    r?.fld_StoresIssuedetid ?? r?.fld_storesIssuedetid ?? r?.storesIssueDetId ?? r?.detId ?? r?.detailId ?? 0,
    0
  ),
  storesIssueId: toInt(
    r?.fld_StoresIssueid ?? r?.fld_storesIssueid ?? r?.storesIssueId ?? r?.issueId ?? 0,
    0
  ),
  materialCode: toStr(r?.fld_itemcode ?? r?.itemCode ?? r?.materialCode),
  materialName: toStr(r?.fld_itemname ?? r?.itemName ?? r?.materialName),
  uom: toStr(r?.fld_uomname ?? r?.uomName ?? r?.uom),
  qty: toStr(r?.fld_fifoqty ?? r?.fifoQty ?? r?.qty),
  price: toStr(r?.fld_fifoprice ?? r?.fifoPrice ?? r?.price),
  amount: toStr(r?.fld_itemamnt ?? r?.itemAmount ?? r?.amount),
  cumulativeAmount: toStr(r?.fld_cumamnt ?? r?.cumAmount ?? r?.cumulativeAmount),
  date: toStr(r?.fld_date ?? r?.date),
});

const AutoList = ({ show, items, onPick, isDark }) => {
  if (!show) return null;
  return (
    <div
      className={`absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border shadow ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      {items.length === 0 ? (
        <div className={`px-3 py-2 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>No matches</div>
      ) : (
        items.map((it, idx) => (
          <button
            key={`${it.value}-${idx}`}
            type="button"
            onClick={() => onPick(it)}
            className={`w-full text-left px-3 py-2 text-sm hover:opacity-90 ${
              isDark ? "text-gray-100 hover:bg-gray-700" : "text-gray-900 hover:bg-gray-100"
            }`}
          >
            {it.label}
          </button>
        ))
      )}
    </div>
  );
};

const BillAddSpares = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const vehId =
    toInt(params.get("VehID")) ||
    toInt(params.get("Vehid")) ||
    toInt(params.get("vehId")) ||
    toInt(params.get("vehicleId")) ||
    0;

  const rawType = (params.get("Type") || params.get("type") || params.get("brtype") || "H").toUpperCase();
  const vehType = rawType === "O" ? "O" : "H";

  const STORAGE_KEY = useMemo(() => `billGen_selection_${vehId}`, [vehId]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const [header, setHeader] = useState({
    vehicleNumber: "",
    period: "",
    toDate: "",
    basis: "",
    vehicleType: "",
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState({});
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);
  const [showCodeSug, setShowCodeSug] = useState(false);
  const [showNameSug, setShowNameSug] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const codeTimer = useRef(null);
  const nameTimer = useRef(null);
  const isBusy = pageLoading || gridLoading || billingLoading;

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;
  const panelClass = `p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`;
  const panelTitleClass = `text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`;
  const ghostBtn = `flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
    isDark
      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
  }`;
  const primaryBtn = `flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`;

  const checkedCount = useMemo(() => Object.keys(selected).length, [selected]);

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const validateDates = useCallback(() => {
    const { fromDate, toDate } = filters;
    if (fromDate && !isDDMMYYYY(fromDate)) return canUse(false, "From date must be dd/MM/yyyy");
    if (toDate && !isDDMMYYYY(toDate)) return canUse(false, "To date must be dd/MM/yyyy");
    if (fromDate && parseDDMMYYYY(fromDate) == null) return canUse(false, "Invalid From date");
    if (toDate && parseDDMMYYYY(toDate) == null) return canUse(false, "Invalid To date");
    if (fromDate && toDate) {
      const f = parseDDMMYYYY(fromDate);
      const t = parseDDMMYYYY(toDate);
      if (t.getTime() < f.getTime()) return canUse(false, "Please select from date less than to date");
    }
    return true;
  }, [filters, canUse]);

  const getStoresIssueDetId = useCallback(
    (r) => toInt(r?.storesIssueDetId ?? r?.fld_StoresIssuedetid ?? r?.fld_storesIssuedetid ?? r?.detId ?? r?.detailId ?? 0, 0),
    []
  );
  const getStoresIssueId = useCallback(
    (r, idx) => toInt(r?.storesIssueId ?? r?.fld_StoresIssueid ?? r?.fld_storesIssueid ?? r?.issueId ?? r?.id ?? idx + 1, 0),
    []
  );
  const getRowKey = useCallback(
    (r, idx) => `${getStoresIssueDetId(r)}|${getStoresIssueId(r, idx)}`,
    [getStoresIssueDetId, getStoresIssueId]
  );

  const saveSelectionToLocalStorage = useCallback(
    (sparesIds, sparesDetIds, sparesTotalAmount) => {
      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            ...existing,
            vehicleId: vehId,
            sparesIds: Array.isArray(sparesIds) ? sparesIds.map((x) => Number(x) || 0) : [],
            sparesDetIds: Array.isArray(sparesDetIds) ? sparesDetIds.map((x) => Number(x) || 0) : [],
            sparesDeduction: String(sparesTotalAmount ?? existing?.sparesDeduction ?? "0"),
            sparesTotalAmount: String(sparesTotalAmount ?? existing?.sparesTotalAmount ?? "0"),
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        /* ignore */
      }
    },
    [STORAGE_KEY, filters, vehId]
  );

  const goToBillGenerationWithData = useCallback(
    (handoff, sparesIds, sparesDetIds, sparesTotalAmount) => {
      const payload = {
        vehId,
        vehicleId: vehId,
        vehType,
        ...handoff,
        sparesIds,
        sparesDetIds,
        sparesDeduction: String(sparesTotalAmount ?? 0),
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };
      sessionStorage.setItem("BillGen_SparesHandoff", JSON.stringify(payload));
      saveSelectionToLocalStorage(sparesIds, sparesDetIds, sparesTotalAmount);
      navigate(`/vehicle/BillGeneration?VehID=${encodeURIComponent(vehId)}&brtype=${encodeURIComponent(vehType)}`, {
        state: payload,
      });
    },
    [vehId, vehType, filters, navigate, saveSelectionToLocalStorage]
  );

  const fetchAuto = useCallback(
    async (kind, prefix) => {
      const url =
        kind === "code"
          ? `${API_BASE}/autocomplete/materialCode`
          : `${API_BASE}/autocomplete/materialName`;
      const { data } = await axios.get(url, {
        params: { prefix: prefix || "" },
        headers: commonHeaders,
      });
      if (!data?.success) return [];
      const raw = data.list || data.rows || [];
      const rows =
        raw.length > 0 && typeof raw[0] === "string"
          ? raw.map((s) => ({ value: s, label: s }))
          : raw.map((x) => ({
              value: toStr(x?.value ?? x?.code ?? x?.name ?? x),
              label: toStr(x?.label ?? x?.code ?? x?.name ?? x),
            }));
      const seen = new Set();
      return rows.filter((it) => {
        if (seen.has(it.value)) return false;
        seen.add(it.value);
        return true;
      });
    },
    [commonHeaders]
  );

  const onMaterialCodeChange = useCallback(
    (value) => {
      setFilters((p) => ({ ...p, materialCode: value }));
      setShowCodeSug(true);
      if (codeTimer.current) clearTimeout(codeTimer.current);
      codeTimer.current = setTimeout(async () => {
        try {
          setCodeSug(await fetchAuto("code", value));
        } catch {
          setCodeSug([]);
        }
      }, 250);
    },
    [fetchAuto]
  );

  const onMaterialNameChange = useCallback(
    (value) => {
      setFilters((p) => ({ ...p, materialName: value }));
      setShowNameSug(true);
      if (nameTimer.current) clearTimeout(nameTimer.current);
      nameTimer.current = setTimeout(async () => {
        try {
          setNameSug(await fetchAuto("name", value));
        } catch {
          setNameSug([]);
        }
      }, 250);
    },
    [fetchAuto]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;
    if (!canUse(vehId, "VehID missing in URL. Use ?VehID=79&Type=3")) return;

    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { vehicleId: vehId, branchId, loginUserId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setHeader((p) => ({
        ...p,
        vehicleNumber: data?.vehicleNumber || data?.vehicleNo || data?.vehicleRegNo || "",
      }));
      setFilters((p) => ({ ...p, materialCode: toStr(data?.defaultMaterialCode || "") }));

      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const sel = JSON.parse(raw);
          const sIds = Array.isArray(sel?.sparesIds) ? sel.sparesIds : [];
          const sDet = Array.isArray(sel?.sparesDetIds) ? sel.sparesDetIds : [];
          if (sIds.length) {
            const map = {};
            for (let i = 0; i < sIds.length; i++) {
              const detId = toInt(sDet[i], 0) || 0;
              const issueId = toInt(sIds[i], 0) || 0;
              map[`${detId}|${issueId}`] = { storesIssueDetId: detId, storesIssueId: issueId };
            }
            setSelected(map);
          } else setSelected({});
        } else setSelected({});
      } catch {
        setSelected({});
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Initialization failed");
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, vehId, commonHeaders, canUse, STORAGE_KEY]);

  const loadList = useCallback(async () => {
    if (!validateDates()) return;
    if (!canUse(vehId, "VehID missing in URL")) return;
    try {
      setGridLoading(true);
      setHasSearched(true);
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          vehicleId: vehId,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          materialCode: filters.materialCode,
          materialName: filters.materialName,
          branchId,
          loginUserId,
        },
        headers: commonHeaders,
      });
      if (!data?.success) {
        setList([]);
        toast.warn(data?.message || "No data found");
        return;
      }
      const rows = (data?.rows || data?.list || []).map(mapRow);
      setList(rows);
      if (!rows.length) toast.info("No Records found");
    } catch (err) {
      console.error(err);
      setList([]);
      toast.error(err?.response?.data?.message || err.message || "Failed to load list");
    } finally {
      setGridLoading(false);
    }
  }, [validateDates, canUse, vehId, filters, branchId, loginUserId, commonHeaders]);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setList([]);
    setHasSearched(false);
  }, []);

  const toggleRow = useCallback(
    (row, idx) => {
      const storesIssueDetId = getStoresIssueDetId(row) || 0;
      const storesIssueId = getStoresIssueId(row, idx) || 0;
      const key = `${storesIssueDetId}|${storesIssueId}`;
      setSelected((p) => {
        const next = { ...p };
        if (next[key] !== undefined) delete next[key];
        else next[key] = { storesIssueDetId, storesIssueId };
        return next;
      });
    },
    [getStoresIssueDetId, getStoresIssueId]
  );

  const allSelected =
    list.length > 0 && list.every((r, idx) => selected[getRowKey(r, idx)] !== undefined);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const map = {};
    list.forEach((r, idx) => {
      const detId = getStoresIssueDetId(r) || 0;
      const issueId = getStoresIssueId(r, idx) || 0;
      map[`${detId}|${issueId}`] = { storesIssueDetId: detId, storesIssueId: issueId };
    });
    setSelected(map);
  }, [allSelected, list, getStoresIssueDetId, getStoresIssueId]);

  const handleRefresh = useCallback(async () => {
    setList([]);
    setCodeSug([]);
    setNameSug([]);
    await loadInit();
  }, [loadInit]);

  const handleBack = useCallback(() => {
    navigate(`/vehicle/BillGeneration?VehID=${vehId}&brtype=${vehType}`);
  }, [navigate, vehId, vehType]);

  const onBill = useCallback(async () => {
    const selectedRows = Object.values(selected || {});
    const sparesIds = selectedRows.map((x) => toInt(x?.storesIssueId, 0)).filter((x) => x > 0);
    if (!canUse(sparesIds.length > 0, "Select at least one spare")) return;
    const sparesDetIds = selectedRows.map((x) => toInt(x?.storesIssueDetId, 0) || 0);

    const selectedPayload = selectedRows.map(({ storesIssueDetId, storesIssueId }) => {
      const row = list.find(
        (r) =>
          toInt(r?.storesIssueDetId, 0) === toInt(storesIssueDetId, 0) &&
          toInt(r?.storesIssueId, 0) === toInt(storesIssueId, 0)
      );
      return {
        storesIssueDetId: toInt(storesIssueDetId, 0) || 0,
        storesIssueId: toInt(storesIssueId, 0) || 0,
        itemAmount: numOr0(row?.amount ?? row?.itemAmount ?? row?.fld_itemamnt),
      };
    });

    try {
      setBillingLoading(true);
      const { data } = await axios.post(
        `${API_BASE}/add`,
        { loginUserId, vehicleId: vehId, type: vehType, selected: selectedPayload },
        { headers: commonHeaders }
      );
      if (!data?.success) throw new Error(data?.message || "Add failed");
      toast.success(data?.message || "Spares selected");
      goToBillGenerationWithData(
        {
          BVehSparesID: data?.BVehSparesID,
          BVehSparesDetID: data?.BVehSparesDetID,
          BVehSparesTotal: data?.BVehSparesTotal,
        },
        sparesIds,
        sparesDetIds,
        data?.BVehSparesTotal ?? 0
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Bill error");
    } finally {
      setBillingLoading(false);
    }
  }, [selected, list, canUse, loginUserId, vehId, vehType, commonHeaders, goToBillGenerationWithData]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (!t.closest?.("[data-autobox='code']")) setShowCodeSug(false);
      if (!t.closest?.("[data-autobox='name']")) setShowNameSug(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  useEffect(() => {
    return () => {
      if (codeTimer.current) clearTimeout(codeTimer.current);
      if (nameTimer.current) clearTimeout(nameTimer.current);
    };
  }, []);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION - ADD SPARES</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Bill Generation - Add Spares
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} • User ID: {loginUserId || "-"} • Vehicle ID: {vehId || "-"} • Selected: {checkedCount}
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={handleRefresh} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button type="button" onClick={() => setShowFilters((s) => !s)} className={ghostBtn}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button type="button" onClick={handleBack} disabled={isBusy} className={`${ghostBtn} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={panelClass}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={panelTitleClass}>Search Criteria</h3>
                <div className="flex gap-2">
                  <button type="button" onClick={handleClearFilters} className={ghostBtn}>
                    Clear
                  </button>
                  <button type="button" onClick={loadList} disabled={isBusy} className={`${primaryBtn} !px-4 !py-2 ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                    <FaSearch /> {gridLoading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input name="fromDate" value={filters.fromDate} onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))} className={fieldClass} placeholder="dd/MM/yyyy" disabled={isBusy} />
                </div>
                <div>
                  <label className={labelClass}>To Date</label>
                  <input name="toDate" value={filters.toDate} onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))} className={fieldClass} placeholder="dd/MM/yyyy" disabled={isBusy} />
                </div>
                <div className="relative" data-autobox="code">
                  <label className={labelClass}>Material Code</label>
                  <input value={filters.materialCode} onChange={(e) => onMaterialCodeChange(e.target.value)} className={fieldClass} placeholder="Material Code" disabled={isBusy} onFocus={() => setShowCodeSug(true)} />
                  <AutoList show={showCodeSug && clean(filters.materialCode).length > 0} items={codeSug} isDark={isDark} onPick={(it) => { setFilters((p) => ({ ...p, materialCode: it.value })); setShowCodeSug(false); }} />
                </div>
                <div className="relative" data-autobox="name">
                  <label className={labelClass}>Material Name</label>
                  <input value={filters.materialName} onChange={(e) => onMaterialNameChange(e.target.value)} className={fieldClass} placeholder="Material Name" disabled={isBusy} onFocus={() => setShowNameSug(true)} />
                  <AutoList show={showNameSug && clean(filters.materialName).length > 0} items={nameSug} isDark={isDark} onPick={(it) => { setFilters((p) => ({ ...p, materialName: it.value })); setShowNameSug(false); }} />
                </div>
                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <div className={fieldClass}>{header.vehicleNumber || "-"}</div>
                </div>
                <div>
                  <label className={labelClass}>Period</label>
                  <div className={fieldClass}>{header.period || "-"}</div>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {gridLoading || pageLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : list.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-center text-sm font-semibold w-16">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 cursor-pointer" title="Select All" />
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Material Code</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Material Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">UOM</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Qty</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Price</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Cumulative Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {list.map((row, idx) => {
                    const key = getRowKey(row, idx);
                    const checked = selected[key] !== undefined;
                    return (
                      <tr
                        key={`${key}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark ? "bg-gray-800" : "bg-white"
                            : isDark ? "bg-gray-750" : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className="px-6 py-4 text-center">
                          <input type="checkbox" checked={checked} onChange={() => toggleRow(row, idx)} className="w-4 h-4 cursor-pointer" disabled={isBusy} />
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{row.materialCode || "-"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-white" : "text-gray-900"}`}>{row.materialName || "-"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.uom || "-"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.qty || "-"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.price || "-"}</td>
                        <td className={`px-6 py-4 text-sm font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>{row.amount || "-"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.cumulativeAmount || "-"}</td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>{row.date || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {list.length > 0 && (
            <div className="p-4">
              <button
                type="button"
                onClick={onBill}
                disabled={isBusy || checkedCount === 0}
                className={`${primaryBtn} !px-4 !py-2 ${checkedCount === 0 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <FaFileInvoiceDollar /> {billingLoading ? "Processing..." : "Add to Bill"}
              </button>
            </div>
          )}

          {isBusy && (
            <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading ? "Loading page..." : gridLoading ? "Searching..." : billingLoading ? "Adding to bill..." : "Loading..."}
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillAddSpares;